import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import activityTracker from '../../utils/activityTracker';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { completeOrder, checkPaymentStatus } from '../../services/paymentService';
import { getOrderById } from '../../services/orderService';
import { CONSENT_CHANGED_EVENT } from '../../utils/analyticsConsent';
import { trackPurchase } from '../../utils/marketingAnalytics';
import { COLORS, FONTS } from '../../theme/tokens';
import { PANEL_SX, PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX } from '../../theme/v2Styles';
import { useTranslation } from 'react-i18next';

const OrderSuccess = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const orderId = queryParams.get('order_id');
  const stripeSession = queryParams.get('stripe_session');
  const { isAuthenticated } = useContext(AuthContext);
  
  // If we have a stripe_session, store it in localStorage so paymentService can access it
  useEffect(() => {
    if (stripeSession) {
      try {
        localStorage.setItem('stripe_session_id', stripeSession);
      } catch (storageError) {
        console.error('Unable to persist stripe session id:', storageError);
      }
    }
  }, [stripeSession]);
  
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [purchaseTrackable, setPurchaseTrackable] = useState(false);
  
  useEffect(() => {
    // If no orderId in URL, redirect to home
    if (!orderId) {
      navigate('/');
      return;
    }
    
    const processOrder = async () => {
      try {
        setLoading(true);
        
        
        // Always try to complete the order first - this will ensure the confirmation email is sent
        // The backend will verify if the payment is actually complete
        setProcessingPayment(true);
        
        // First, check the order status to avoid duplicate processing
        try {
          // Get the order details first
          const orderDetails = await getOrderById(orderId);
          
          // Only call completeOrder if the order is in "Created" status (status_id = 6)
          if (orderDetails && orderDetails.status_id === 6) {
            // Pass stripeSession to the backend for verification
            const completeResult = await completeOrder(orderId, stripeSession); 
          } else {
          }
        } catch (completeError) {
          console.error('Error completing order:', completeError);
          // Continue processing even if there's an error
        }
        
        // Now check payment status to determine what to show the user
        const paymentStatus = await checkPaymentStatus(orderId, stripeSession);
        
        // Get the order details
        const orderDetails = await getOrderById(orderId);
        setOrder(orderDetails);
        
        // If payment is not started, redirect to checkout
        if (paymentStatus.status === 'not_started') {
          navigate('/checkout');
          return;
        }
        
        // For completed payments, clear the cart
        if (paymentStatus.status === 'completed' ||
            paymentStatus.status === 'paid' ||
            paymentStatus.status === 'paid_with_gift_card') {

          // Track order complete
          if (orderDetails) {
            activityTracker.trackOrderComplete({
              id: orderDetails.id,
              total: orderDetails.final_price || orderDetails.total
            });
            trackPurchase(orderDetails);
            setPurchaseTrackable(true);
          }

          // Create and dispatch a special cart reset event that will force a full refresh
          // and reset the cart count to zero instead of just refreshing
          const cartResetEvent = new CustomEvent('cartReset', { detail: { forceZero: true } });
          window.dispatchEvent(cartResetEvent);

          // // Also dispatch regular cartUpdated event for compatibility - REMOVED to prevent race condition
          // window.dispatchEvent(new Event('cartUpdated'));

          // Clear any stored cart session data
          try {
            localStorage.removeItem('cart_items');
            localStorage.removeItem('cart_count');
          } catch (storageError) {
            console.error('Unable to clear stored cart data:', storageError);
          }
        }
      } catch (error) {
        
        setError(error.message || t('orders.processError'));
      } finally {
        setLoading(false);
        setProcessingPayment(false);
      }
    };
    
    processOrder();
  }, [orderId, navigate]);

  useEffect(() => {
    if (!purchaseTrackable || !order) {
      return undefined;
    }

    const handleConsentChanged = () => {
      trackPurchase(order);
    };

    window.addEventListener(CONSENT_CHANGED_EVENT, handleConsentChanged);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, handleConsentChanged);
  }, [purchaseTrackable, order]);
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          {t('orders.processingOrder')}
        </Typography>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/')}>
          {t('orders.returnHome')}
        </Button>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 7 } }}>
      <Paper elevation={0} sx={{ ...PANEL_SX, p: { xs: 3, md: 5 }, textAlign: 'center', bgcolor: COLORS.surfacePanel }}>
        <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'rgba(58,79,65,0.10)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 34, color: COLORS.green }} />
        </Box>
        
        <Typography component="h1" sx={{ fontFamily: FONTS.heading, fontWeight: 600, fontSize: { xs: '1.625rem', md: '2rem' }, mb: 1.5 }}>
          {t('orders.thankYou')}
        </Typography>
        
        <Typography variant="body1" paragraph>
          {t('orders.placedSuccessfully')}
        </Typography>
        
        <Typography variant="body1" paragraph>
          {t('orders.orderNumberInline')}<strong>{order?.order_number}</strong>
        </Typography>
        
        <Divider sx={{ my: 3 }} />
        
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/')}
            sx={SECONDARY_BUTTON_SX}
          >
            {t('orders.continueShopping')}
          </Button>
          
          {isAuthenticated && (
            <Button 
              variant="contained" 
              onClick={() => navigate('/my-orders')}
              sx={PRIMARY_BUTTON_SX}
            >
              {t('orders.viewMyOrders')}
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default OrderSuccess;
